import React, { useCallback, useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { CheckCircle2, Loader2, Plus, Save, ShieldAlert, Tags, Trash2, XCircle } from 'lucide-react';
import api from '@/lib/api';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const VIDE = { nom_cuvee: '', couleur: 'rouge', categorie: 'AOP', appellation: '', millesime: 2025, cepages: '', volume_cl: 75, tav: 14, pays: 'France',
  embouteilleur_nom: '', embouteilleur_adresse: '', mention_embouteillage: 'Mis en bouteille à la propriété', numero_lot: '', allergenes: ['sulfites'],
  ingredients: 'Raisins, conservateur : sulfites', sucres_g: 0.2, effervescent: false, teneur_sucre: '', bio: false, code_certificateur: '',
  logo_femme_enceinte: true, triman: true };
const TEXTES = [['nom_cuvee', 'Nom de la cuvée'], ['appellation', 'Appellation'], ['cepages', 'Cépages'], ['embouteilleur_nom', 'Embouteilleur'],
  ['embouteilleur_adresse', 'Commune et pays'], ['mention_embouteillage', 'Mention d\'embouteillage'], ['numero_lot', 'N° de lot'], ['ingredients', 'Ingrédients (e-label)']];
const NOMBRES = [['millesime', 'Millésime'], ['volume_cl', 'Volume (cl)'], ['tav', 'TAV (% vol)'], ['sucres_g', 'Sucres (g/100 ml)']];
const BASCULES = [['bio', 'Vin bio'], ['logo_femme_enceinte', 'Logo femme enceinte'], ['triman', 'Info-tri Triman'], ['effervescent', 'Effervescent']];

function Apercu({ e, calc }) {
  return (
    <div className="mx-auto w-72 bg-[#fbf8f1] border border-stone-300 shadow-lg p-5 text-center font-serif" data-testid="etiquette-apercu">
      <p className="text-xs tracking-[0.3em] uppercase text-stone-500">{e.embouteilleur_nom || 'Votre domaine'}</p>
      <p className="text-2xl mt-3 text-[#5b1a2a]">{e.nom_cuvee || 'Nom de la cuvée'}</p>
      <p className="text-sm mt-1">{e.appellation}</p>
      {e.categorie === 'AOP' && <p className="text-[10px] uppercase tracking-wider">Appellation {e.appellation} Protégée</p>}
      <p className="text-3xl mt-3">{e.millesime || ''}</p>
      <div className="flex justify-between text-sm mt-4 border-t border-stone-300 pt-2"><span>{e.tav} % vol</span><span>{e.volume_cl} cl</span></div>
      <p className="text-[10px] mt-2">{e.mention_embouteillage} par {e.embouteilleur_nom}, {e.embouteilleur_adresse}</p>
      <p className="text-[10px]">Produit de {e.pays} · {e.numero_lot ? `Lot ${e.numero_lot}` : <span className="text-red-600">Lot ?</span>}</p>
      <p className="text-[10px] font-bold">Contient des {e.allergenes?.join(', ')}</p>
      <div className="flex items-center justify-center gap-3 mt-2 text-[10px]">
        <span>E = {calc?.energie_kcal_100ml ?? '…'} kcal / 100 ml</span>
        {e.logo_femme_enceinte && <span className="border border-stone-500 rounded-full px-1" title="Femme enceinte">⊘ enceinte</span>}
        {e.bio && <span className="bg-green-600 text-white px-1 rounded">BIO {e.code_certificateur}</span>}
        {e.triman && <span className="border px-1">Triman</span>}
      </div>
    </div>
  );
}

function Controles({ v }) {
  if (!v) return null;
  return (
    <div className="space-y-1" data-testid="etiquette-controles">
      <p className={`font-semibold flex items-center gap-2 ${v.conforme ? 'text-green-700' : 'text-red-700'}`} data-testid="etiquette-conformite">
        {v.conforme ? <CheckCircle2 className="w-5 h-5" /> : <ShieldAlert className="w-5 h-5" />}
        {v.conforme ? 'Étiquette conforme' : `${v.manquants.length} mention(s) à corriger`}
      </p>
      {v.controles.map((c) => (
        <div key={c.mention} className="flex gap-2 text-sm">
          {c.ok ? <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />}
          <div><span className="font-medium">{c.mention}</span><span className="text-stone-500"> — {c.detail}</span></div>
        </div>
      ))}
    </div>
  );
}

function Formulaire({ e, set }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {TEXTES.map(([k, l]) => (
        <label key={k} className="text-xs text-stone-600 space-y-1">{l}
          <Input value={e[k] || ''} onChange={(x) => set({ ...e, [k]: x.target.value })} data-testid={`etiquette-${k}`} /></label>
      ))}
      {NOMBRES.map(([k, l]) => (
        <label key={k} className="text-xs text-stone-600 space-y-1">{l}
          <Input type="number" step="0.1" value={e[k] ?? ''} onChange={(x) => set({ ...e, [k]: x.target.value === '' ? null : parseFloat(x.target.value) })} data-testid={`etiquette-${k}`} /></label>
      ))}
      {e.bio && <label className="text-xs text-stone-600 space-y-1">Code certificateur
        <Input value={e.code_certificateur} onChange={(x) => set({ ...e, code_certificateur: x.target.value })} placeholder="FR-BIO-01" data-testid="etiquette-code_certificateur" /></label>}
      <div className="sm:col-span-2 flex flex-wrap gap-4">
        {BASCULES.map(([k, l]) => (
          <label key={k} className="flex items-center gap-2 text-sm"><Switch checked={!!e[k]} onCheckedChange={(c) => set({ ...e, [k]: c })} data-testid={`etiquette-${k}`} />{l}</label>
        ))}
      </div>
    </div>
  );
}

export default function EtiquetageVinPage() {
  const { activeSociete } = useWorkspace();
  const [liste, setListe] = useState([]);
  const [e, setE] = useState(null);
  const [calc, setCalc] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    const r = await api.get('/etiquetage-vin');
    setListe(r.data.etiquettes);
    return r.data.etiquettes;
  }, []);
  useEffect(() => {
    if (!activeSociete?.id) return;
    setE(null);
    charger().then((l) => setE(l[0] || { ...VIDE })).catch(() => toast.error('Étiquettes indisponibles'));
  }, [charger, activeSociete?.id]);
  const supprimer = async () => {
    if (!e.id || !window.confirm(`Supprimer l'étiquette « ${e.nom_cuvee} » ?`)) return;
    try {
      await api.delete(`/etiquetage-vin/${e.id}`);
      toast.success('Étiquette supprimée');
      const l = await charger();
      setE(l[0] || { ...VIDE });
    } catch { toast.error('Échec de la suppression'); }
  };

  useEffect(() => {
    if (!e) return undefined;
    const t = setTimeout(() => api.post('/etiquetage-vin/verifier', e).then((r) => setCalc(r.data)).catch(() => setCalc(null)), 350);
    return () => clearTimeout(t);
  }, [e]);

  const sauver = async () => {
    setEnvoi(true);
    try {
      const r = e.id ? await api.put(`/etiquetage-vin/${e.id}`, e) : await api.post('/etiquetage-vin', e);
      toast.success('Étiquette enregistrée');
      await charger();
      setE(r.data);
    } catch { toast.error("Échec de l'enregistrement"); }
    setEnvoi(false);
  };

  if (!e) return <div className="p-8"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;
  const lien = e.id ? `${window.location.origin}/e-label/${e.id}` : null;
  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="etiquetage-page">
      <div className="flex flex-wrap justify-between items-end gap-3">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-3"><Tags className="w-7 h-7 text-[#5b1a2a]" />Étiquetage réglementaire</h1>
          <p className="text-sm text-stone-500 mt-1">Mentions obligatoires vérifiées en direct, valeur énergétique calculée, QR code e-label (ingrédients et nutrition).</p>
        </div>
        <Button variant="outline" onClick={() => setE({ ...VIDE })} data-testid="etiquette-nouvelle"><Plus className="w-4 h-4 mr-2" />Nouvelle étiquette</Button>
      </div>
      <div className="flex gap-2 flex-wrap">
        {liste.map((x) => (
          <button key={x.id} onClick={() => setE(x)} data-testid={`etiquette-onglet-${x.id}`}
            className={`px-3 py-1.5 rounded-full text-sm border ${e.id === x.id ? 'bg-[#5b1a2a] text-white' : 'bg-white'}`}>
            {x.nom_cuvee} {x.verification?.conforme ? '✓' : '⚠'}
          </button>
        ))}
      </div>
      <div className="grid lg:grid-cols-5 gap-6">
        <Card className="lg:col-span-3"><CardContent className="p-5 space-y-5">
          <Formulaire e={e} set={setE} />
          <Button onClick={sauver} disabled={envoi || !e.nom_cuvee} data-testid="etiquette-enregistrer">
            {envoi ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Enregistrer
          </Button>
          {e.id && <Button variant="outline" className="ml-2 border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800" onClick={supprimer} data-testid="etiquette-supprimer"><Trash2 className="w-4 h-4 mr-2" />Supprimer</Button>}
          <Controles v={calc?.verification} />
        </CardContent></Card>
        <div className="lg:col-span-2 space-y-4">
          <Apercu e={e} calc={calc} />
          <Card><CardContent className="p-4 flex items-center gap-4">
            {lien ? <QRCodeSVG value={lien} size={96} data-testid="etiquette-qrcode" /> : <div className="w-24 h-24 bg-stone-100 rounded" />}
            <div className="text-sm">
              <p className="font-medium">QR code e-label</p>
              {lien ? <a href={lien} target="_blank" rel="noreferrer" className="text-purple-700 underline break-all" data-testid="etiquette-lien-elabel">Ouvrir la page consommateur</a>
                : <p className="text-stone-500">Enregistrez l'étiquette pour générer le QR code.</p>}
            </div>
          </CardContent></Card>
        </div>
      </div>
    </div>
  );
}
